/**
 * Como o Dashboard apresenta KPI cuja leitura falhou.
 *
 * O defeito que isto conserta é o do `CLAUDE.md`, na letra: *"KPI 'N sessões
 * ativas' em um CP offline — quando o CP não responde, o número é herdado"*.
 * A `DashboardPage` só tratava erro quando **não havia** dado (`error &&
 * !kpis`); com a tela já preenchida, o 401 do logout e o 500 do servidor
 * eram engolidos e os quatro números continuavam ali, do mesmo tamanho e da
 * mesma cor de antes, como leitura viva. Números de antes apresentados como
 * atuais.
 *
 * É a mesma classe do contador do menu (D-004) — distinguir "não há" de
 * "não sei" — e a resposta aqui é a mesma: o dado continua na tela, porque
 * o último valor conhecido é útil, mas para de se apresentar como atual.
 *
 * ## Dois modos de falha, não um
 *
 *  - `sessao-encerrada` (401) — o servidor recusou a leitura. A sessão
 *    morreu em outra aba, expirou, ou o usuário foi desativado. Quem
 *    derruba a UI para o login é o canal `/ws/live` (fechamento 1008, ver
 *    `useLiveEvents.ts`); esta tela não decide sessão, só para de mentir
 *    enquanto isso não acontece.
 *  - `sem-sinal` (todo o resto: 500, 502, rede caída, JSON torto) — a
 *    sessão pode estar viva e a fonte é que não respondeu.
 *
 * `403` cai deliberadamente em `sem-sinal`: para a tela, "perdi o módulo" e
 * "o servidor está com problema" são indistinguíveis, e apresentar recusa
 * como conjunto vazio é um defeito próprio, já catalogado como P-4.
 */

export type EstadoFonteKpi = 'viva' | 'sem-sinal' | 'sessao-encerrada';

/**
 * Classifica a fonte a partir do erro que o `useKpis` guardou.
 *
 * O `useKpis` lança `new Error(String(res.status))` em resposta não-ok e
 * propaga a mensagem do `fetch` em falha de rede, então aqui chega `'401'`,
 * `'500'`, `'Failed to fetch'`... Só o `'401'` tem tratamento próprio: é o
 * único em que o servidor afirma algo sobre a SESSÃO, e não sobre o dado.
 */
export function classificarFonteKpi(erro: string | null): EstadoFonteKpi {
  if (!erro) return 'viva';
  return erro === '401' ? 'sessao-encerrada' : 'sem-sinal';
}

/**
 * Sufixo de classe do card. Um lugar só, porque os quatro KPIs têm de
 * contar a mesma história — e porque assim a regra fica testável sem
 * renderizador React (o pacote não tem jsdom).
 */
export function classeDeFonteKpi(estado: EstadoFonteKpi): string {
  return estado === 'viva' ? '' : ' nx-kpi--sem-sinal';
}

/**
 * Texto do aviso acima da grade. `null` quando não há o que avisar.
 *
 * Sempre diz DE QUANDO é o número — é o que o padrão STALE do `CLAUDE.md`
 * pede com o rótulo "(último)", e é o que transforma "não sabemos" em
 * informação acionável: 14:31 de dois minutos atrás é uma coisa, de ontem é
 * outra.
 */
export function avisoDeFonteKpi(
  estado: EstadoFonteKpi,
  horaDaUltimaLeitura: string | null,
): string | null {
  if (estado === 'viva') return null;
  const quando = horaDaUltimaLeitura
    ? `Os números abaixo são a última leitura, das ${horaDaUltimaLeitura}.`
    : 'Os números abaixo são a última leitura recebida.';
  const causa =
    estado === 'sessao-encerrada'
      ? 'Sessão encerrada — o servidor recusou a leitura dos indicadores.'
      : 'Sem sinal — os indicadores pararam de ser atualizados.';
  return `${causa} ${quando}`;
}

/**
 * Texto da tela que NÃO tem dado nenhum — onde o erro é a tela inteira.
 *
 * Existe porque `Erro ao carregar KPIs: 401` não diz a ninguém que a sessão
 * acabou, e `...: timeout` não diz que a fonte não respondeu. Quando o
 * código não é nenhum dos conhecidos, mostra o código cru: inventar texto
 * amigável para erro desconhecido esconde justamente o que ajudaria a
 * diagnosticar.
 */
export function erroSemDadoKpi(erro: string): string {
  if (classificarFonteKpi(erro) === 'sessao-encerrada') {
    return 'Sessão encerrada — entre novamente para ver os indicadores.';
  }
  if (erro === 'timeout') {
    return 'A fonte dos indicadores não respondeu a tempo. Tentando de novo…';
  }
  return `Erro ao carregar KPIs: ${erro}`;
}

/** Rótulo curto que substitui a dica do card. Mesmo papel do "(último)". */
export function dicaDeFonteKpi(horaDaUltimaLeitura: string | null): string {
  return horaDaUltimaLeitura ? `último às ${horaDaUltimaLeitura}` : 'último valor recebido';
}
